import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  SimpleChanges,
  OnChanges,
  HostListener,
  ChangeDetectorRef,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { NavItemModel } from 'src/app/sharedFeatures/models/nav-item-model';
import { UserLoggedIn } from 'src/app/sharedFeatures/models/user-login.model';
import { AuthGuard } from 'src/app/sharedFeatures/services/auth-guard.service';
import { CurrentUserService } from 'src/app/sharedFeatures/services/current-user.service';
import { NavigationService } from 'src/app/sharedFeatures/services/navigation.service';
import { Icons } from 'src/assets/svgs/index.ts';
import { Logos } from 'src/assets/svgs/logos';

@Component({
  selector: 'msn-side-bar',
  templateUrl: './side-bar.component.html',
  styleUrls: ['./side-bar.component.scss'],
})
export class SideBarComponent implements OnInit, OnChanges, OnDestroy {
  navList: NavItemModel[] = [];
  currentUser: UserLoggedIn | null = null;
  @Input() Refresh: boolean = false;
  subscriptions: Subscription[] = [];
  activeIndex: number | null = null;
  icons = Icons as any;
  logos = Logos;
  logoLink: string = '/dashboard';
  private routerSubscription?: Subscription;
  currentRoute: string = '';
  private navLoadRetryCount: number = 0;
  private maxNavLoadRetries: number = 3;

  constructor(
    private navigationService: NavigationService,
    public adminGuard: AuthGuard,
    private currentUserService: CurrentUserService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.currentUser = this.currentUserService.getCurrentUser();

    this.getNavItems();
    let currentUrl = this.router.url;
    if (currentUrl === '/') {
      if (this.currentUser?.moduleId === 2) {
        this.activeIndex = this.navList.findIndex(
          item => item.link === '/msm-checklist/general-dashboard/divisions'
        );
      } else if (this.currentUser?.moduleId === 1) {
        this.activeIndex = this.navList.findIndex(
          item => item.link === '/dashboard'
        );
      }
      // this.activeIndex = this.navList.findIndex(
      //   item => item.link === '/dashboard'
      // );
    }
  }

  ngOnInit(): void {
    this.currentUser = this.currentUserService.getCurrentUser();

    this.getNavItems();
    this.currentUserService
      .isTokenExchangeCompleted()
      .subscribe(isExchanged => {
        if (isExchanged) {
          // Reset retry count when token exchange completes
          this.navLoadRetryCount = 0;
          this.getNavItems(); // Re-fetch menu after token exchange
        }
      });

    // Listen to route changes to update parent active state
    this.currentRoute = this.router.url;
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        // Update current route to trigger change detection
        this.currentRoute = event.url;
        this.cdr.markForCheck();

        // If navList is empty and user is logged in, try to reload
        this.checkAndReloadNavItems();
      });

    // Check after initial load - retry if empty (handles page refresh scenarios)
    setTimeout(() => {
      this.checkAndReloadNavItems();
    }, 500);
  }

  /**
   * Check if nav items are empty and try to reload if user is logged in
   */
  private checkAndReloadNavItems(): void {
    if (
      this.navList.length === 0 &&
      this.currentUserService.isLoggedIn() &&
      this.navLoadRetryCount < this.maxNavLoadRetries
    ) {
      console.warn(
        `Nav items empty, attempting reload (attempt ${this.navLoadRetryCount + 1}/${this.maxNavLoadRetries})`
      );
      this.navLoadRetryCount++;
      this.getNavItems();
    }
  }

  ngOnDestroy(): void {
    // Clean up router subscription to prevent memory leaks
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }
  getNavItems() {
    this.navigationService.getNavItems().subscribe(res => {
      this.navList = res.sort((a, b) => a.order - b.order);
      this.navList.sort((a, b) => a.order - b.order); // Re-sort to maintain order

      // Initialize expanded property for all items
      this.navList.forEach(item => {
        (item as any).expanded = false;
      });

      // Reset retry count on successful load with items
      if (this.navList.length > 0) {
        this.navLoadRetryCount = 0;
      }

      // Trigger change detection
      this.cdr.detectChanges();
    });
  }

  navigateToLogoLink(): void {
    this.currentUser = this.currentUserService.getCurrentUser();

    if (!this.currentUser) {
      return;
    }

    if (this.currentUser.moduleId === 2) {
      window.location.assign(this.currentUser?.moduleLink); // Open in same tab
    } else {
      this.router.navigate(['/dashboard']); // Navigate within the app
    }
  }

  toggleSubmenu(item: any, event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (item.childs?.length) {
      // Close all other submenus
      this.navList.forEach(navItem => {
        if (navItem !== item) {
          navItem.expanded = false;
        }
      });

      // Toggle current submenu (no sidebar expansion)
      item.expanded = !item.expanded;

      // Position submenu after DOM updates
      if (item.expanded && event) {
        const targetElement = event.currentTarget as HTMLElement;
        setTimeout(() => {
          this.positionSubmenu(targetElement);
        }, 0);
      }
    }
  }

  positionSubmenu(parentElement: HTMLElement) {
    const submenuElement = parentElement.parentElement?.querySelector(
      '.dct-submenu'
    ) as HTMLElement;
    if (!submenuElement) {
      console.log('Submenu element not found');
      return;
    }

    const parentRect = parentElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const margin = 8;

    // Cap height first so the natural measurement reflects the available space.
    submenuElement.style.maxHeight = `${viewportHeight - 2 * margin}px`;
    submenuElement.style.overflowY = 'auto';

    // Anchor to the parent, then shift up if the panel would overflow the viewport.
    submenuElement.style.top = `${parentRect.top}px`;
    const submenuHeight = submenuElement.offsetHeight;
    let top = parentRect.top;
    if (top + submenuHeight > viewportHeight - margin) {
      top = Math.max(margin, viewportHeight - submenuHeight - margin);
    }
    submenuElement.style.top = `${top}px`;
  }

  onChildItemClick() {
    // Close the submenu when a child is clicked
    // Parent remains highlighted via isParentActive() method which checks if current route matches any child
    this.navList.forEach(item => {
      item.expanded = false;
    });
  }

  setActive(index: number) {
    this.activeIndex = index;
    // Close all submenus when clicking on a regular menu item
    this.navList.forEach(item => {
      item.expanded = false;
    });
  }

  isParentActive(item: NavItemModel): boolean {
    if (!item.childs || item.childs.length === 0) {
      return false;
    }
    const best = this.getBestMatchLink();
    if (best) {
      // A specific nav item owns this URL; only that item's parent activates.
      return item.childs.some(child => child.link === best);
    }
    // No explicit nav link matched (e.g. a sub-route that doesn't appear in
    // the menu). Fall back to the parent's module prefix so we still
    // highlight the right module (e.g. "/msm-checklist/action-plan/items-list"
    // activates the Action Plan parent).
    const prefix = this.getModulePrefix(item);
    if (!prefix) {
      return false;
    }
    const currentUrl = this.currentRoute || this.router.url;
    return (
      currentUrl === prefix ||
      currentUrl.startsWith(prefix + '/') ||
      currentUrl.startsWith(prefix + '?')
    );
  }

  private getModulePrefix(item: NavItemModel): string | null {
    if (!item.childs || item.childs.length === 0) {
      return null;
    }
    const links = item.childs.map(c => c.link).filter((l): l is string => !!l);
    if (links.length === 0) {
      return null;
    }
    const segments = links.map(l => l.split('/'));
    const minLen = Math.min(...segments.map(s => s.length));
    const common: string[] = [];
    for (let i = 0; i < minLen; i++) {
      const seg = segments[0][i];
      if (segments.every(s => s[i] === seg)) {
        common.push(seg);
      } else {
        break;
      }
    }
    // Require at least leading '' + domain + module segments so the prefix
    // is meaningful (e.g. '/msm-checklist/action-plan'); otherwise bail.
    if (common.length < 3) {
      return null;
    }
    return common.join('/');
  }

  private getBestMatchLink(): string | null {
    const currentUrl = this.currentRoute || this.router.url;
    let bestLink: string | null = null;
    let bestLen = -1;

    const consider = (link?: string) => {
      if (!link) return;
      const matches =
        currentUrl === link ||
        currentUrl.startsWith(link + '/') ||
        currentUrl.startsWith(link + '?');
      if (matches && link.length > bestLen) {
        bestLink = link;
        bestLen = link.length;
      }
    };

    for (const item of this.navList) {
      consider(item.link);
      if (item.childs) {
        for (const child of item.childs) {
          consider(child.link);
        }
      }
    }
    return bestLink;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    // Check if click is outside the sidebar and submenu
    const target = event.target as HTMLElement;
    const clickedInsideSidebar = target.closest('.dct-main-menu');
    const clickedInsideSubmenu = target.closest('.dct-submenu');

    if (!clickedInsideSidebar && !clickedInsideSubmenu) {
      // Close all submenus
      this.navList.forEach(item => {
        item.expanded = false;
      });
    }
  }
}
